/**
 * The Atlas's navigation: the rung the altitude reads as, the state a rung,
 * body or cell change applies, the zoom verbs, and the jump another panel
 * asks for through `atlas:navigate`.
 */

import type * as THREE from 'three';

import type { AtlasLive, AtlasState } from '../atlas';
import { track } from '../visits/bus.ts';
import type { FocusSource } from '../visits/events.ts';
import { altitudeForTier } from './camera.ts';
import { SHIP_MARKER_ID } from './shipMarker.ts';
import { regionOffsetLatLon } from './regionFrame.ts';
import { all as allStructures } from './structures.ts';
import type { ZoomLadderHandle } from './zoomLadder.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import type { ExposureHandle } from './exposure.ts';
import type { FrameGovernorHandle } from './frameGovernor.ts';
import type { HexViewHandle } from './hexView.ts';
import type { InspectorHandle } from './inspector.ts';
import type { SiteChipsHandle } from './panels/siteChips.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { BodySwitchHandle } from './bodySwitch.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { MountHandle } from './mount.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import type { SiteRegistryState } from './siteRegistry.ts';

export interface NavigationDeps {
  readonly state: AtlasState;
  readonly detailGroup: THREE.Group;
  readonly ladder: ZoomLadderHandle;
  readonly mount: MountHandle;
  readonly focusRig: FocusRigHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly cameraRig: CameraRigHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly exposureHandle: ExposureHandle;
  readonly frameGovernor: FrameGovernorHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly siteChips: SiteChipsHandle;
  readonly bodyScene: BodySceneHandle;
  readonly bodySwitch: BodySwitchHandle;
  readonly siteRegistry: SiteRegistryState;
  readonly live: AtlasLive;
  saveState(s: AtlasState): void;
  siteUrl(): string | null;
  /** Built after this module, so atlas.ts hands these over through getters. */
  readonly overlayView: OverlayViewHandle;
  readonly inspector: InspectorHandle;
}

export interface NavigationHandle {
  syncTierToAltitude(): void;
  applyState(opts?: { reframe?: boolean; altM?: number; aim?: { lat: number; lon: number } | null }): void;
  focusShip(): void;
  zoomIntoBody(id: string, source?: FocusSource): void;
  zoomIntoCell(id: string): void;
  zoomOut(): void;
  navigateTo(target: { lat: number; lon: number; bodyId?: string } | null): void;
  onAtlasNavigate(e: Event): void;
}

export function createNavigation(deps: NavigationDeps): NavigationHandle {
  const { state, detailGroup, ladder, descentLoad, siteChips, saveState, siteUrl } = deps;
  const { surface } = deps.mount;
  const { focusCam, tierOfAltitude, shipFocused, fitOpening } = deps.focusRig;
  const { landBodyApplied } = deps.bodyScene;
  const { syncBody } = deps.bodySwitch;
  const { rowById, ladderBody, registryDatumM, descendable } = deps.bodyReaders;
  const { applyTierFraming, aspect, enterPlotRegime, surfaceRegime, stopCinema } = deps.cameraRig;
  const { availableQuad, regionAnchorLatLon } = deps.descentAnchorHandle;
  const { armGroundWarm, loadClaim } = descentLoad;
  const { setAltitude } = deps.exposureHandle;
  const { setRenderScale } = deps.frameGovernor;
  const { quadLines, hexOverlay, syncHexVisibility } = deps.hexViewSeam;

  /**
   * Altitude decides the rung, so crossing an edge is the only thing that
   * changes one. It changes a label, the inspector's density and what the
   * streamer is asked for, and it does not reframe: the camera is already where
   * the player put it.
   */
  function syncTierToAltitude(): void {
    const next = tierOfAltitude();
    if (next === state.tier) return;
    const wasPlot = surfaceRegime();
    state.tier = next;
    track('atlas_tier', { body_id: state.bodyId ?? 'sol', band: deps.focusRig.band(), tier: next });
    // Descending past REGIONAL aims the ladder at the surveyed site, which is
    // what used to be a hardcoded route through three tiers. The cell itself
    // is not read here: `hexOverlay.anchorCell()` at the instant of a tier
    // change can still be the PREVIOUS rung's cell, since the overlay's own
    // build is time-sliced across frames - `syncHexOverlay` fills `cellId` in
    // once the drawn resolution has actually caught up to this altitude.
    if (next >= 2 && state.quadId === null) state.quadId = availableQuad()?.id ?? null;
    // Each rung's own segment goes when the rung does, not both at tier 1: a
    // cell id kept at tier 2 is what put a Mars hex slug in Europa's
    // breadcrumb, reachable there through `?atlasTier=6` on a body whose floor
    // clamps the camera back to tier 2 on the next frame.
    if (next < 3) state.cellId = null;
    if (next < 2) state.quadId = null;
    applyState({ reframe: false });
    if (!wasPlot && surfaceRegime()) enterPlotRegime();
  }
  // State + transitions
  function applyState(
    opts: { reframe?: boolean; altM?: number; aim?: { lat: number; lon: number } | null } = {},
  ): void {
    // Keyed on the LADDER's body and not `state.bodyId`: everything `syncBody`
    // builds belongs to the body the ladder is on, so focusing the one the URL
    // already opened must not rebuild it mid-descent.
    // `siteBodyId` beside it because `setWorldBody` moves `appliedBodyId` with
    // the globe cruise draws and no build behind it: without this second half a
    // LAND onto the body cruise arrived at opens the last body's sites, which
    // is a Jezero claim on Earth. `selectSiteBody` runs nowhere but `syncBody`
    // and the registry's own load, so this is exactly "the build has not run
    // for the body the ladder is on".
    if (deps.bodySwitch.state.appliedBodyId !== ladderBody()
      || (!landBodyApplied && deps.siteRegistry.siteBodyId !== ladderBody())) syncBody();
    // A focus is what starts the ground's own reads now, so every path that
    // changes which body is focused arms the warm on its way through here.
    armGroundWarm();
    // Every path that changes tier/body/quad/cell routes through here, so this
    // is the one place that reliably clears a stale not-in-slice card before
    // the new state renders.
    deps.inspector.clearFlash();
    saveState(state);
    // 3.56 MB of sunspot record, fetched the first time a player opens the Sun
    // and never at boot; the disc draws its limb darkening without it.
    if (state.bodyId === 'sol') deps.bodyScene.state.orrery?.loadSolPhotosphere();
    // A click or a shared link asks for a rung's framing; crossing a band edge
    // on the way down does not - the camera is already where the player put it.
    if (opts.reframe !== false) applyTierFraming(state.tier, opts.altM, opts.aim);
    // Cursor hint: drag orbits the focus at every altitude, including the
    // heliocentric view, which used to early-return on both drag and wheel.
    surface.style.cursor = 'grab';
    // Visibility per tier. The overlays belong to the body with baked ground
    // under them, which is the registry's own `availability.descend`.
    const onGround = descendable();
    // Both retire at the CLAIM edge: a quadrangle outline is 1,000 km of line
    // on the datum sphere and a hex ring is 1.2 km of it, and below 25 km the
    // leaves are the ground those lines would be buried in.
    quadLines.visible = onGround && state.tier >= 2 && state.tier <= 4;
    // The grid is no longer pinned to two rungs: it draws from ORBIT down on
    // any body whose row carries a resolution, at whichever one the altitude
    // selects, and `detailGroup` already holds it off at SYSTEM.
    syncHexVisibility();
    detailGroup.visible = ladder.orreryOn && onGround && state.tier >= 1;
    // Orbit rings are SYSTEM decoration, and their vertices are AU-scale floats
    // that quantise to kilometres once the camera is close to one of them.
    deps.bodyScene.state.orrery?.setRingsVisible(state.tier <= 2);
    // The claim's SURVEY - placements, verdicts, addresses - is asked for a
    // rung early so the panel is populated before the CLAIM edge. The mesh is
    // not: the pyramid's own leaves are the ground at CLAIM and PLOT, so what
    // this fetch buys is numbers and nothing waits on it.
    const askTier = descentLoad.state.claimTierLate ? 4 : 3;
    if (state.tier >= askTier) {
      // The rung that asked, not the rung `ensureSites` resolves on: under a
      // throttle that read is seconds of its own, and what this records is
      // where the descent decided it wanted the claim.
      const gateTier = state.tier;
      void siteChips.ensureSites().then(() => {
        const url = siteUrl();
        if (url !== null && state.tier >= askTier) {
          if (descentLoad.state.claimAskedAtTier === null) descentLoad.state.claimAskedAtTier = gateTier;
          void loadClaim(url);
        }
      });
    }
    // A state application that changed what is drawn and did not ask for a
    // frame is how entering T2 used to leave the quad lattice invisible for
    // three seconds: nothing was moving, so the on-demand loop drew nothing.
    deps.live.needsRender = true;
    // A band change is a new workload, so the governor starts over from 1.0
    // rather than carrying the last band's average into this one.
    setRenderScale(1, `tier ${state.tier}`);
    deps.overlayView.renderOverlay();
  }

  /** Where a click on the marker parks the camera, metres from the ship. */
  const SHIP_FOCUS_ALTITUDE_M = 5000;

  /**
   * Take the camera to the ship.
   *
   * Not a rung. The marker has no registry row, no quadrangles and no ground
   * under it, so the ladder's own body and tier are left where they are and
   * only the focus camera is pointed somewhere else; `focusBodies` answers for
   * the id, and the floor it gives holds every altitude inside the marker's own
   * SYSTEM edge, so the wheel and the drag behave the way they do around a
   * planet and the band never reads as a descent.
   */
  function focusShip(): void {
    if (deps.bodyScene.state.shipMarker === null || !deps.bodyScene.state.shipMarker.known) return;
    stopCinema();
    focusCam.goTo(SHIP_MARKER_ID, null, null, SHIP_FOCUS_ALTITUDE_M);
    focusCam.setOrientation(focusCam.state.yaw, 0.35);
    deps.live.cameraDirty = true;
    deps.live.needsRender = true;
    deps.overlayView.renderOverlay();
  }

  function zoomIntoBody(id: string, source: FocusSource = 'orrery'): void {
    const row = rowById(id);
    if (!row) {
      deps.inspector.flashInspector(`${id} is not a catalogued body in this slice.`);
      return;
    }
    track('atlas_focus', { body_id: id, body_class: row.class.replace(/^\d_/, ''), source });
    // A browse or a click did not come from LAND, so a point an earlier LAND
    // named on this body belongs to that descent, not this one: dropped
    // directly, since a same-body reclick leaves `appliedBodyId` unchanged
    // below and `syncBody()` never reruns to read a cleared `focusCam.state`
    // back into `descentFor` - and dropped from `focusCam.state` too, so a
    // later body change does not resurrect it there either. Otherwise
    // `namedLanding` in descentAnchor.ts pins this visit to a session that
    // already ended.
    descentLoad.state.descentFor = null;
    focusCam.setAnchor(null, null);
    deps.descentAnchorHandle.landing = null;
    deps.descentAnchorHandle.standAt = null;
    state.bodyId = id;
    deps.bodySwitch.openSiteAt(null);
    state.quadId = null;
    state.cellId = null;
    // Every body is orbitable now, whether or not it has ground: the registry's
    // own `availability` is what stops the descent, and it stops it at the
    // altitude where the whole disc is in frame rather than at a card.
    state.tier = 1;
    applyState();
    if (!row.availability.descend) {
      deps.inspector.flashInspector(`${row.display_name} is catalogued as a ${row.class.replace(/^\d_/, '')}: `
        + `${row.availability.reason}`);
    }
  }
  function zoomIntoCell(id: string): void {
    state.tier = 3; state.cellId = id;
    applyState();
  }
  /**
   * Up one rung.
   *
   * There is no ascent switch left: the rung is a function of altitude, so
   * leaving one is climbing to the altitude of the one above. Escape, the
   * breadcrumb and the right-click all land here.
   */
  function zoomOut(): void {
    stopCinema();
    if (state.tier === 0) {
      // The ship is the one thing tier 0 can be focused on, so up from it is
      // the whole system rather than nothing.
      if (shipFocused()) {
        fitOpening(aspect());
        deps.live.cameraDirty = true;
        deps.live.needsRender = true;
        deps.overlayView.renderOverlay();
      }
      return;
    }
    if (state.tier === 6) state.tileId = null;
    if (state.tier === 1) {
      state.bodyId = null;
      state.tier = 0;
      applyState();
      return;
    }
    const row = rowById(state.bodyId);
    if (!row) return;
    setAltitude(altitudeForTier(state.tier - 1, row.radius_m));
  }

  // External navigation — lets other panels (colony, crafting) jump the atlas
  // straight to region level with an oblique over-the-shoulder camera.
  //
  // With ground named, that ground; with none, the settlement, which is what
  // the colony panel's own jump has always asked for. A named body not
  // already open switches state.bodyId and shares syncBody's rebuild through
  // applyState() below, not zoomIntoBody itself, so the fly-to lands on the
  // right planet rather than on the ladder's own at that lat/lon.
  function navigateTo(target: { lat: number; lon: number; bodyId?: string } | null): void {
    const targetBody = target?.bodyId && rowById(target.bodyId) ? target.bodyId : ladderBody();
    state.bodyId = targetBody;
    deps.bodySwitch.openSiteAt(target);
    state.quadId = availableQuad()?.id ?? null;
    state.cellId = hexOverlay.anchorCell();
    state.tileId = null;
    state.tier = 5;
    applyState();

    const anchor = regionAnchorLatLon();
    let lat = target?.lat ?? anchor?.lat ?? 0;
    let lon = target?.lon ?? anchor?.lon ?? 0;
    // Only the placements a plot has resolved have a position to average.
    const structs = allStructures().filter(
      (p): p is typeof p & { x: number; z: number } => p.x !== undefined && p.z !== undefined,
    );
    if (target === null && structs.length > 0 && anchor) {
      const cx = structs.reduce((s, p) => s + p.x, 0) / structs.length;
      const cz = structs.reduce((s, p) => s + p.z, 0) / structs.length;
      ({ lat, lon } = regionOffsetLatLon(
        deps.live.plotField?.meta.frame, registryDatumM(targetBody), anchor.lat, anchor.lon, cx, cz,
      ));
    }
    focusCam.goTo(targetBody, lat, lon, 400);
    focusCam.setOrientation(0.6, 0.55);
    // After `applyState`, whose `syncBody` reads the pre-jump focus into `descentFor`.
    deps.descentAnchorHandle.standAt = target ? { body: targetBody, lat, lon } : null;
    if (target) {
      descentLoad.state.descentFor = { body: targetBody, lat, lon };
      deps.bodySwitch.releasePatchOffDescent();
    }
    deps.live.cameraDirty = true;
  }

  function onAtlasNavigate(e: Event): void {
    const detail = (e as CustomEvent<{ lat?: number; lon?: number; bodyId?: string }>).detail;
    const at = detail && Number.isFinite(detail.lat) && Number.isFinite(detail.lon)
      ? { lat: detail.lat as number, lon: detail.lon as number, bodyId: detail.bodyId }
      : null;
    navigateTo(at);
  }

  return {
    syncTierToAltitude, applyState, focusShip, zoomIntoBody, zoomIntoCell, zoomOut, navigateTo, onAtlasNavigate,
  };
}
